const defaultTheme = require('tailwindcss/defaultTheme');
const animate = require('tailwindcss-animate');

const colors = require(`./public/solutions/${process.env.VUE_APP_SOLUTION_PATH}/colors`);

module.exports = {
    content: ['./public/index.html', './src/**/*.vue', './src/**/*.ts', './node_modules/@makeabledk/**/*.vue'],
    theme: {
        extend: {
            transitionDuration: {
                2000: '2000ms',
            },
            transitionProperty: {
                width: 'width',
                'margin-left': 'margin-left',
                height: 'height',
                borderRadius: 'border-radius',
            },
            willChange: {
                height: 'height',
            },
            fontFamily: {
                sans: ['Noto Sans', ...defaultTheme.fontFamily.sans],
            },
            spacing: {
                2.5: '0.625rem',
                2.75: '0.6875rem',
                6.5: '1.625rem',
                16.5: '4.125rem',
                17: '4.25rem',
                19: '4.75rem',
                21: '5.25rem',
                42: '10.5rem',
                46: '12.5rem',
                76: '17rem',
                84: '21rem',
                88: '22rem',
                92: '23rem',
                100: '25rem',
                112: '28rem',
                120: '30rem',
                128: '32rem',
                160: '40rem',
                228: '57rem',
                240: '60rem',
                256: '64rem',
                280: '70rem',
                288: '72rem',
                dashboard: '1500px',
            },
            minHeight: (theme) => ({
                ...theme('spacing'),
            }),

            minWidth: (theme) => ({
                ...theme('spacing'),
            }),

            maxWidth: (theme) => ({
                ...theme('spacing'),
                screen: '100vw',
            }),

            maxHeight: (theme) => ({
                ...theme('spacing'),
            }),
            backgroundOpacity: {
                35: '0.35',
            },
            colors: {
                ...colors,
                gray: {
                    ...defaultTheme.colors.gray,
                    50: '#f9fafb',
                },
                green: {
                    ...defaultTheme.colors.green,
                    200: '#91C391',
                    600: '#CDDDDD',
                },
                // The treatment domain's deep indigo (Behandling and Genbehandling entries). Outside the
                // per-solution colour files for the same reason as `chart` below: it identifies a kind of
                // registration, not a customer, so it must read the same in every brand - whitelabel
                // carries it as `health`, but the other solutions do not reliably define that key.
                treatment: '#37286E',
                // Chart domain colours, mirroring the backend's own chart strategies so a metric
                // keeps one identity across the server-rendered charts and the client-rendered ones
                // (CrownData-API: WeightChartUtils.AvgDataColor/SensorColors,
                // WaterConsumptionChartStrategy.COLOR_WATER, ChartStrategyBase.COLOR_NOTE).
                // Deliberately outside the per-solution colour files: these identify a measured
                // quantity, not a customer, so unlike `primary` they do not vary by brand - and the
                // existing domain keys there (pigs, deaths, feed, ...) are already byte-identical in
                // all four files, which is the duplication this avoids repeating.
                // Everything else the charts need already exists as a theme token and is referenced
                // from `plugins/highcharts/chartColors.ts` rather than restated here.
                chart: {
                    // The Insertion series' light pink. Its own token rather than a reuse of `pigs`:
                    // sibling arrival/departure pages land next to this one and each needs a colour
                    // of its own, and `pigs` already means "a count of pigs" wherever else it is used.
                    insertion: '#FFB9C8',
                    // The Transfer series' deep indigo, a sibling of `insertion`. Separate from the
                    // top-level `transfer`, which is the transfer domain's colour elsewhere in the
                    // app: this one identifies a series on a records chart, and the two are free to
                    // move independently.
                    transfer: '#37286E',
                    // The Sold series' teal, a sibling of `insertion`/`transfer` on the same records
                    // charts. Matches the departure/`ic_leave` registration icon's own colour (#005151)
                    // so the chart reads as the same event type the row-detail modal shows.
                    sold: '#005151',
                    // The Deaths records chart's series colour, same hex as the top-level `deaths`
                    // domain token - both already mean "a count of dead pigs" - but its own key, same
                    // as `insertion` and `transfer` above, so the series is free to move independently.
                    death: '#E3563A',
                    // The Slaughters records chart's main series (pig count), a sibling of
                    // `insertion`/`transfer`/`sold`/`death` above. Not the same hex as the top-level
                    // `slaughters` token (#E6A439, already plotted by pigsStatus/reportsPage for the
                    // same quantity) - deliberately its own colour, so the two charts draw
                    // slaughtered-pig counts in two different colours on purpose.
                    slaughters: '#F0A202',
                    // The same chart's secondary series (discarded pig count), plotted on its own
                    // y-axis and toggled off by default - see `useSlaughtersChartOptions`.
                    slaughtersDiscarded: '#F45E73',
                    weight: '#38943C',
                    dailyGain: '#8BD35F',
                    growthIndex: '#1B5D07',
                    water: '#3ABEFF',
                    note: '#FFCC00',
                    noteFill: '#FEF6D5',
                    noteText: '#7A5C00',
                    // The Production Group context band's tinted chip: pale fill, saturated outline,
                    // dark ink. A teal of its own rather than a step of `sky` or `cyan` - it has to
                    // stay clear of the water series' blue directly above it while still reading as
                    // the same chip the Section table draws for a Production Group.
                    //
                    // These names are load-bearing and unchecked: `plugins/tailwind/index.ts` casts
                    // this config to a hand-written type, so a name that drifts from the type
                    // compiles clean and resolves to `undefined` at runtime.
                    productionGroupBand: '#B2E8F1',
                    productionGroupBandBorder: '#0891A6',
                    productionGroupBandText: '#075E6B',
                    // The per-camera weight lines drawn alongside a Section's aggregate, indexed by the
                    // camera's ordinal. Eight entries for a design ceiling of six, so a Section beyond
                    // the ceiling still gets its own colour before the list wraps.
                    //
                    // Inside the weight domain's own green-to-teal family rather than across the
                    // spectrum: a camera line measures the quantity its aggregate does, so at six lines
                    // it has to read as one band around it. None repeats `weight`, `dailyGain` or
                    // `growthIndex` above, and none strays into the water blue or the reference red.
                    //
                    // Ordered light-dark alternating rather than as a ramp, so adjacent ordinals differ
                    // in lightness as well as hue and stay apart in greyscale. Hue never separates a
                    // camera from its aggregate - see cameraSeries.ts for the stroke and dash that do.
                    cameras: ['#2E8B77', '#A3C46D', '#407E8C', '#96B84C', '#1F6F5C', '#6DBFAF', '#4F8A6B', '#7FB069'],
                    // The Delivered feed pr. week chart's per-Feed-Mix palette, taken in turn by the
                    // mixes it plots the way `cameras` above is taken in turn by camera ordinal.
                    // Eight entries for the same reason: the dropdown can check more than the default
                    // top 5, and a mix beyond the ceiling still gets its own colour before the list
                    // wraps. An
                    // amber/gold family of its own - it identifies the feed domain the way `weight`'s
                    // green family identifies weight, and stays clear of `insertion`/`transfer`/
                    // `sold`/`death` above, which already mean pig-registration series.
                    feedDeliveries: ['#B98B26', '#E8C468', '#8C6D1F', '#D9A441', '#7A5C1E', '#F0D8A0', '#A67C27', '#C9A227'],
                    // The medicine Log's treatments pr. week chart, all treatments in one series: the
                    // treatment domain's own indigo, same hex as the top-level `treatment` (the
                    // Behandling badges) and as `transfer` above, but its own key so the series is
                    // free to move independently.
                    treatments: '#37286E',
                    // The same chart per drug, taken in turn by the drugs it plots the way
                    // `feedDeliveries` is taken by Feed Mix. Starts on `treatments` and stays in its
                    // indigo-violet family, so either view reads as the treatment domain; ordered
                    // light-dark alternating so adjacent drugs differ in lightness as well as hue.
                    treatmentsByDrug: ['#37286E', '#8A79D6', '#5B4A9E', '#B7A9EC', '#251A4F', '#6F5FC0', '#9D8FDB', '#46377F'],
                },
                'dark-gray': {
                    100: 'rgba(245,246,250,0.63)',
                    200: '#F5F6FA',
                    600: 'rgba(22,17,33,0.8)',
                    800: '#161121',
                },
                red: {
                    ...defaultTheme.colors.red,
                    400: '#F5492B',
                },
                sky: {
                    50: '#f0f9ff',
                    100: '#e0f2fe',
                    200: '#bae6fd',
                    300: '#7dd3fc',
                    400: '#38bdf8',
                    500: '#0ea5e9',
                    600: '#0284c7',
                    700: '#0369a1',
                    800: '#075985',
                    900: '#0c4a6e',
                    950: '#082f49',
                },
                success: {
                    50: '#f1fcf4',
                    100: '#defae9',
                    200: '#c0f3d3',
                    300: '#8de8b0',
                    400: '#54d486',
                    500: '#2dba64',
                    600: '#209950',
                    700: '#1c7941',
                    800: '#1b6037',
                    900: '#184f2f',
                    950: '#082b17',
                },
                error: {
                    50: '#fef4f2',
                    100: '#fde7e3',
                    200: '#fddbd5',
                    300: '#fab3a7',
                    400: '#f48875',
                    500: '#ea6149',
                    600: '#d7452b',
                    700: '#b53620',
                    800: '#95311f',
                    900: '#7c2e20',
                    950: '#43140c',
                },
                warning: {
                    50: '#fef8ee',
                    100: '#fbe6c6',
                    200: '#f8d9b0',
                    300: '#f4be7d',
                    400: '#ee9849',
                    500: '#ea7c25',
                    600: '#db631b',
                    700: '#b64b18',
                    800: '#913c1b',
                    900: '#753319',
                    950: '#3f180b',
                },
            },
            fontSize: {
                xs: ['0.6875rem', '1rem'],
                sm: ['0.8125rem', '1.1875rem'],
                normal: ['0.875rem', '1.25rem'],
                base: ['1rem', '1.5rem'],
                lg: ['1.125rem', '1.625rem'],
                xl: ['1.5rem', '2.25rem'],
                '2xl': ['1.75rem', '2.625rem'],
                '3xl': ['2rem', '2.875rem'],
            },
            screens: {
                print: {
                    raw: 'print',
                },
            },
        },

        container: () => ({
            center: true,
        }),
    },
    variants: {
        opacity: ['responsive', 'hover', 'focus', 'disabled'],
        extend: {
            padding: ['first', 'last'],
            borderRadius: ['first', 'last'],
            margin: ['first', 'last'],
            borderWidth: ['first', 'last'],
            backgroundColor: ['odd', 'even'],
        },
    },
    plugins: [animate],
    important: true,
};
